<template>
  <q-dialog
    :model-value="modelValue"
    persistent
    maximized
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <q-card class="tender-quote-dialog">
      <!-- Dialog Header -->
      <q-card-section class="dialog-header bg-secondary">
        <div class="row items-center">
          <div class="col">
            <div class="text-h5 text-weight-bold text-white">
              {{ isEdit ? 'Edit Quote' : 'Create New Quote' }}
            </div>
            <div class="text-body2 text-blue-2">
              {{ isEdit ? 'Update quote details' : 'Create a new quote for this tender' }}
            </div>
          </div>
          <div class="col-auto">
            <q-btn
              flat
              round
              icon="close"
              size="md"
              text-color="white"
              @click="onCancel"
            />
          </div>
        </div>
      </q-card-section>

      <!-- Dialog Content -->
      <q-card-section class="dialog-content">
        <q-form ref="quoteForm" class="q-gutter-lg" @submit="onSubmit">

          <!-- Quote Details -->
          <div class="form-section">
            <div class="section-header">
              <q-icon name="description" color="secondary" size="24px" class="q-mr-sm" />
              <div class="section-title">Quote Details</div>
            </div>

            <div class="section-content">
              <div class="row q-col-gutter-md">
                <!-- Tender Info Banner -->
                <div class="col-12">
                  <q-banner class="bg-blue-1 text-grey-8" rounded>
                    <template #avatar>
                      <q-icon name="info" color="primary" />
                    </template>
                    <div class="row items-center q-gutter-md">
                      <div>
                        <span class="text-weight-medium">Site:</span>
                        {{ siteName }}
                      </div>
                      <div v-if="contractorCount > 0">
                        <span class="text-weight-medium">Contractors:</span>
                        {{ contractorCount }}
                      </div>
                    </div>
                    <div class="text-caption q-mt-xs">
                      This quote will be linked to the tender's site automatically
                    </div>
                  </q-banner>
                </div>

                <!-- Quote Name (option_name) -->
                <div class="col-12">
                  <q-input
                    v-model="form.option_name"
                    label="Quote Name *"
                    outlined
                    :rules="[val => !!val || 'Quote name is required']"
                    hint="E.g., Standard Option, Premium Option, Base Bid"
                    class="enhanced-input"
                  >
                    <template #prepend>
                      <q-icon name="description" />
                    </template>
                  </q-input>
                </div>
              </div>
            </div>
          </div>

          <!-- Quote Configuration -->
          <div class="form-section">
            <div class="section-header">
              <q-icon name="settings" color="primary" size="24px" class="q-mr-sm" />
              <div class="section-title">Tender Configuration</div>
            </div>

            <div class="section-content">
              <div class="row q-col-gutter-md">
                <!-- Primary Quote -->
                <div class="col-12 col-md-6">
                  <q-checkbox
                    v-model="form.is_primary"
                    label="Primary Quote Option"
                    color="primary"
                  />
                  <div class="field-hint">
                    Mark this as the primary quote option for the tender
                  </div>
                </div>

                <!-- Win Likelihood -->
                <div class="col-12 col-md-6">
                  <q-input
                    v-model="form.likelihood"
                    label="Win Likelihood (%)"
                    outlined
                    type="number"
                    class="enhanced-input"
                    :rules="[
                      val => val >= 0 || 'Likelihood must be at least 0%',
                      val => val <= 100 || 'Likelihood cannot exceed 100%'
                    ]"
                  >
                    <template #append>
                      <q-icon name="percent" />
                    </template>
                    <template #prepend>
                      <q-icon name="trending_up" />
                    </template>
                  </q-input>
                </div>

                <!-- Quote Status -->
                <div v-if="isEdit" class="col-12 col-md-6">
                  <q-checkbox
                    v-model="form.is_sent"
                    label="Quote has been sent to contractor"
                    color="positive"
                  />
                  <div class="field-hint">
                    Mark if this quote has been sent to contractors
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Notes -->
          <div class="form-section">
            <div class="section-header">
              <q-icon name="note" color="accent" size="24px" class="q-mr-sm" />
              <div class="section-title">Notes</div>
            </div>

            <div class="section-content">
              <q-input
                v-model="form.notes"
                label="Notes"
                outlined
                type="textarea"
                autogrow
                rows="3"
                hint="Any additional notes or special instructions for this quote"
                class="enhanced-input"
              >
                <template #prepend>
                  <q-icon name="note" />
                </template>
              </q-input>
            </div>
          </div>
        </q-form>
      </q-card-section>

      <!-- Dialog Actions -->
      <q-card-actions class="dialog-actions bg-grey-1">
        <div class="row full-width items-center">
          <div class="col">
            <q-btn
              flat
              icon="help"
              label="Help"
              color="grey-7"
              @click="showHelp"
            />
          </div>
          <div class="col-auto">
            <q-btn-group>
              <q-btn
                flat
                label="Cancel"
                color="grey-7"
                class="action-btn"
                @click="onCancel"
              />
              <q-btn
                :label="isEdit ? 'Update Quote' : 'Create Quote'"
                color="secondary"
                :loading="savingQuote"
                class="action-btn action-btn--primary"
                @click="onSubmit"
              />
            </q-btn-group>
          </div>
        </div>
      </q-card-actions>
    </q-card>

    <!-- Help Dialog -->
    <q-dialog v-model="showHelpDialog">
      <q-card style="width: 600px; max-width: 80vw">
        <q-card-section>
          <div class="text-h6">Quote Creation Help</div>
        </q-card-section>
        <q-card-section>
          <q-list>
            <q-item>
              <q-item-section avatar>
                <q-icon name="description" color="secondary" />
              </q-item-section>
              <q-item-section>
                <q-item-label>Quote Creation</q-item-label>
                <q-item-label caption>
                  Create a new quote for this tender. The quote will be automatically linked to the tender's customer and site.
                </q-item-label>
              </q-item-section>
            </q-item>
            <q-item>
              <q-item-section avatar>
                <q-icon name="label" color="primary" />
              </q-item-section>
              <q-item-section>
                <q-item-label>Option Names</q-item-label>
                <q-item-label caption>
                  Use option names to differentiate between quote variations like "Standard", "Premium", etc.
                </q-item-label>
              </q-item-section>
            </q-item>
            <q-item>
              <q-item-section avatar>
                <q-icon name="trending_up" color="positive" />
              </q-item-section>
              <q-item-section>
                <q-item-label>Win Likelihood</q-item-label>
                <q-item-label caption>
                  Estimate the percentage chance this quote option will win the tender.
                </q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn v-close-popup flat label="Close" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </q-dialog>
</template>

<script setup>
  import { ref, computed, watch, onMounted } from 'vue';
  import { useQuasar } from 'quasar';

  const props = defineProps({
    modelValue: {
      type: Boolean,
      required: true,
    },
    tenderId: {
      type: Number,
      required: true,
    },
    quote: {
      type: Object,
      default: null,
    },
    tender: {
      type: Object,
      default: null,
    },
  });

  const emit = defineEmits(['update:modelValue', 'save', 'cancel']);

  const $q = useQuasar();

  // Refs
  const quoteForm = ref(null);
  const showHelpDialog = ref(false);
  const savingQuote = ref(false);

  // Form state for creating quotes
  const form = ref({
    // site_id is auto-set from the tender
    option_name: '',
    is_primary: false,
    likelihood: 50,
    notes: '',
    is_sent: false,
  });

  // Computed properties
  const isEdit = computed(() => !!props.quote);

  const siteName = computed(() => {
    return props.tender?.site?.name || props.tender?.site_name || 'Site from tender';
  });

  const contractorCount = computed(() => {
    return props.tender?.contractors?.length || props.tender?.contractor_count || 0;
  });

  // Methods
  function resetForm() {
    form.value = {
      // site_id is auto-set from tender on the backend
      option_name: '',
      is_primary: false,
      likelihood: 50,
      notes: '',
      is_sent: false,
    };
  }

  function populateForm(quote) {
    form.value = {
      // site_id is auto-set from tender on the backend
      option_name: quote.option_name || '',
      is_primary: quote.is_primary,
      likelihood: quote.likelihood,
      notes: quote.notes || '',
      is_sent: quote.is_sent || false,
    };
  }

  async function onSubmit() {
    const isValid = await quoteForm.value.validate();
    if (!isValid) {return;}

    savingQuote.value = true;
    try {
      const quoteData = {
        // site_id is auto-set from the tender on the backend
        option_name: form.value.option_name,
        is_primary: form.value.is_primary,
        likelihood: form.value.likelihood,
        notes: form.value.notes || null,
        is_sent: form.value.is_sent || false,
      };

      emit('save', quoteData);
    } catch (error) {
      $q.notify({
        color: 'negative',
        message: `Failed to ${isEdit.value ? 'update' : 'create'} quote: ${error.message}`,
        icon: 'warning',
      });
    } finally {
      savingQuote.value = false;
    }
  }

  function onCancel() {
    emit('cancel');
  }

  function showHelp() {
    showHelpDialog.value = true;
  }

  // Watchers
  watch(() => props.modelValue, (newVal) => {
    if (newVal) {
      // Dialog opened
      if (props.quote) {
        populateForm(props.quote);
      } else {
        resetForm();
      }
    }
  });

  // Lifecycle hooks
  onMounted(() => {
    // Initialize form with tender data
    if (props.quote) {
      populateForm(props.quote);
    } else {
      resetForm();
    }
  });
</script>

<style lang="scss" scoped>
.tender-quote-dialog {
  height: 100vh;
  display: flex;
  flex-direction: column;

  .dialog-header {
    flex-shrink: 0;
    border-radius: 0;
  }

  .dialog-content {
    flex: 1;
    overflow-y: auto;
    padding: 24px;

    .form-section {
      margin-bottom: 32px;

      .section-header {
        display: flex;
        align-items: center;
        margin-bottom: 16px;
        padding-bottom: 8px;
        border-bottom: 2px solid #f0f0f0;

        .section-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: #333;
        }
      }

      .section-content {
        .enhanced-input,
        .enhanced-select {
          :deep(.q-field__control) {
            border-radius: 8px;
          }

          :deep(.q-field__prepend) {
            color: #666;
          }
        }

        .field-hint {
          font-size: 0.8rem;
          color: #666;
          margin-top: 4px;
          line-height: 1.3;
        }
      }
    }
  }

  .dialog-actions {
    flex-shrink: 0;
    padding: 16px 24px;
    border-top: 1px solid #e0e0e0;

    .action-btn {
      min-width: 120px;
      font-weight: 600;
      border-radius: 6px;
      padding: 8px 16px;

      &--primary {
        background: linear-gradient(45deg, #e91e63, #f06292);
        color: white;
      }
    }
  }
}
</style>